import { Circle, Container, EventBoundary, type FederatedPointerEvent, type Texture } from 'pixi.js';
import { BIOMES, isSlotUnlocked, slotGemPrice } from '@goodtime/shared';
import {
  FLY_MS,
  PLATE_SHIFT,
  PaintedMapScene,
  TOP_BAR,
  boxOf,
  type MapInsets,
  type PaintedMapArt,
  type PaintedMapOptions,
  type Placed,
} from './PaintedMapScene';
import { centreOn, openingZoom } from '../logic/realm/camera';
import {
  LANDMARKS,
  WORLD_LABELS,
  WORLDS_WEST_TO_EAST,
  landmarkFor,
  landmarkNode,
  nearestWorld,
  worldCentre,
  worldOf,
  worldSlots,
} from '../logic/realm/places';
import { REALM_TILES, type TileSet } from '../logic/realm/tiles';
import { RealmPlaque, worldLabel } from '../widgets/realm/RealmPieces';
import {
  RealmPlaces,
  type PlaceCardUi,
  type RealmPlacesOptions,
} from '../widgets/realm/RealmPlaces';
import type { MiniGameSnapshot } from '../widgets/minigames/types';
import { REALM_SPOTS, SECRET_SPOTS, spotWorld } from '../logic/realm/spots';

/** Art the app supplies, by URL. */
export type RealmMapArt = PaintedMapArt;

/** The realm map as the overview of the worlds' own maps (flag
 *  `world_maps`). */
export interface RealmOverview {
  /** The worlds with their own map (indexes into BIOMES). */
  worlds: readonly number[];
  /** Open a world's map, after the glide in: centred on a slot (its
   *  building was tapped) or, with null, wherever the host chooses. */
  onOpenWorld: (world: number, nodeId: number | null) => void;
  /** A glide into a world's map has begun: the host can start loading it,
   *  so its files arrive while the camera flies. */
  onEnterWorld?: (world: number) => void;
}

export interface RealmMapSceneOptions extends PaintedMapOptions {
  tiles?: TileSet;
  /** The side places, the secrets, the title plaque, and the HUD's Wheel
   *  and gift (milestone 2); none of them without this. */
  places?: RealmPlacesOptions;
  /** The overview of the worlds' maps: a tap anywhere in a world with a
   *  map (its name, its land or a building) glides in and opens it, and
   *  each world's name is a plate with its open count. Without it, the
   *  realm map is as before: buildings open their slots. */
  overview?: RealmOverview;
}

/** How far below a building's middle its plate hangs, in master pixels. */
const PLAQUE_DROP = 66;
/** A building's tap area, in master pixels across. */
const HIT = 220;
/** How far a glide into a world zooms in. */
const GLIDE_IN = 1.8;

/**
 * The realm map (flag `realm_map`): one painted map holding every world,
 * the home screen in place of the episode list and the world maps. Painted
 * buildings open their slots; the world's name along the bottom opens all
 * its slots. The panning, zooming, tiles, names, floors, ambient life, HUD
 * and boards are the painted map's (PaintedMapScene); the realm adds its
 * buildings, the worlds' names, and the side places, secrets and title.
 */
export class RealmMapScene extends PaintedMapScene<RealmMapSceneOptions> {
  protected override readonly logName = 'RealmMapScene';
  /** The side places' pins, over the buildings' plates. */
  private readonly spotLayer = new Container();
  private places: RealmPlaces | null = null;
  private spotPlates: Placed[] = [];
  private worldNames: Placed[] = [];
  /** The overview mode, when on ({@link setOverview}). */
  private overview: RealmOverview | undefined;
  /** The world being glided into, before its map opens. */
  private entering: number | null = null;
  private enterTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(container: HTMLElement, opts: RealmMapSceneOptions) {
    super(container, opts, opts.tiles ?? REALM_TILES, worldOf(opts.currentNodeId));
    this.overview = opts.overview;
  }

  /** The worlds with their own map, in overview mode. */
  private mapped(world: number): boolean {
    return !!this.overview?.worlds.includes(world);
  }

  /** Turn the overview mode on or off (the flag can arrive after the map
   *  has opened, from the player's fetch). */
  setOverview(overview: RealmOverview | undefined): void {
    const was = this.overview;
    this.overview = overview;
    if (this.state !== 'initialized') return;
    if (!!was === !!overview && was?.worlds.join() === overview?.worlds.join()) return;
    this.labelLayer.visible = !overview;
    this.buildPlates();
    this.placeNames();
    this.updateSwitcher();
  }

  protected override buildMore(): void {
    // A tap on a world's land with a map opens it (overview mode). Taps on
    // buildings, plates, pins and glints are theirs: only a tap on the
    // painting itself is the land's.
    this.world.on('pointertap', (e: FederatedPointerEvent) => this.tapLand(e));
    // The worlds' names, lettered on the painting; in overview mode they
    // are plates instead (buildPlates).
    this.labelLayer.visible = !this.overview;
    this.worldNames = BIOMES.map((b) => {
      const label = worldLabel(b.name);
      const at = WORLD_LABELS[b.id];
      this.labelLayer.addChild(label);
      return {
        item: label,
        mx: at.x * this.map.w,
        my: at.y * this.map.h,
        drop: 0,
        box: boxOf(label),
        maxShift: Math.max(PLATE_SHIFT, label.width / 2),
        tappable: false,
        shown: false,
        fresh: true,
      };
    });
    if (this.opts.places) this.buildPlaces(this.opts.places);
  }

  protected override moreNames(): readonly Placed[] {
    return this.overview ? this.spotPlates : [...this.spotPlates, ...this.worldNames];
  }

  protected override resized(first: boolean): void {
    this.places?.layout(this.view.w, this.view.h);
    if (first) this.places?.showTitle();
  }

  protected override openingSpot(): { x: number; y: number } {
    return landmarkFor(this.opts.currentNodeId) ?? worldCentre(this.currentWorld);
  }

  protected override buildingAt(name: string): { x: number; y: number } | null {
    return LANDMARKS.find((q) => q.name === name) ?? null;
  }

  protected override escapeOverlay(): boolean {
    return this.places?.escape() ?? false;
  }

  protected override covering(): boolean {
    return !!this.places?.covering;
  }

  protected override hostCovered(covered: boolean): void {
    this.places?.setCovered(covered);
  }

  protected override cameraMoved(zoom: number): void {
    this.places?.onCamera(zoom);
  }

  protected override floorIn(world: number, texture: Texture): void {
    this.places?.floorArrived(world, texture);
  }

  protected override disposeMore(): void {
    this.places?.destroy();
    this.places = null;
    if (this.enterTimer) clearTimeout(this.enterTimer);
    this.enterTimer = null;
  }

  // ---- the switcher: the world in view ----

  /** The view came to rest: the switcher names the world nearest its
   *  middle. */
  protected override viewCentred(x: number, y: number): void {
    const world = nearestWorld(x, y);
    if (world !== this.currentWorld) {
      this.currentWorld = world;
      this.updateSwitcher();
    }
  }

  /** The switcher's arrows glide to the next world, west to east. */
  protected override stepSwitcher(step: number): void {
    const order = WORLDS_WEST_TO_EAST;
    const i = order.indexOf(this.currentWorld);
    const next = order[(i + step + order.length) % order.length] ?? 0;
    const c = worldCentre(next);
    this.currentWorld = next;
    this.holdSwitcher = true;
    this.updateSwitcher();
    const zoom = Math.max(this.cam.zoom, openingZoom(this.map, this.view));
    this.flyTo(centreOn(c.x * this.map.w, c.y * this.map.h, zoom, this.map, this.view));
  }

  protected override switcherText(): [string, string] {
    const slots = worldSlots(this.currentWorld);
    const open = slots.filter((n) => isSlotUnlocked(n.id, this.unlocked)).length;
    const name = BIOMES[this.currentWorld]?.name ?? '';
    if (this.mapped(this.currentWorld)) return [`Open ${name}`, `${open} of ${slots.length} open`];
    return [name, `All ${slots.length} slots · ${open} open`];
  }

  /** The switcher's middle opens the world's map, or its board without one. */
  protected override switcherTapped(): void {
    const w = this.currentWorld;
    if (!this.mapped(w)) {
      this.openBoard(w);
      return;
    }
    const c = worldCentre(w);
    this.enterWorld(w, null, c.x * this.map.w, c.y * this.map.h);
  }

  // ---- the overview: into a world's map ----

  /** A tap on the painting itself: the world there opens, if it has a map. */
  private tapLand(e: FederatedPointerEvent): void {
    if (e.target !== this.world || this.dragged) return;
    const p = e.getLocalPosition(this.world);
    const w = nearestWorld(p.x / this.map.w, p.y / this.map.h);
    if (this.mapped(w)) this.enterWorld(w, null, p.x, p.y);
  }

  /** Glide in on a spot of a world (master pixels), then open its map. */
  private enterWorld(world: number, nodeId: number | null, mx: number, my: number): void {
    const overview = this.overview;
    if (!overview || this.dragged || this.entering !== null) return;
    this.entering = world;
    overview.onEnterWorld?.(world);
    // Reduced motion: no glide, the map opens straight away.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (!still) this.focus(mx, my, Math.min(1, this.cam.zoom * GLIDE_IN));
    this.enterTimer = setTimeout(
      () => {
        this.enterTimer = null;
        this.entering = null;
        if (this.state !== 'destroyed') overview.onOpenWorld(world, nodeId);
      },
      still ? 0 : FLY_MS,
    );
  }

  // ---- the buildings ----

  protected override buildPlates(): void {
    for (const c of this.landmarkLayer.removeChildren()) c.destroy({ children: true });
    this.plates = [];
    const plaques: RealmPlaque[] = [];
    const lock = this.texture('lock');
    const gem = this.texture('gem');
    for (const l of LANDMARKS) {
      const node = landmarkNode(l);
      if (!node) continue;
      const open = isSlotUnlocked(node.id, this.unlocked);
      const x = l.x * this.map.w;
      const y = l.y * this.map.h;
      const world = worldOf(node.id);
      const mapped = this.mapped(world);
      const hit = new Container();
      hit.label = `landmark:${l.name}`;
      hit.position.set(x, y);
      hit.hitArea = new Circle(0, 0, HIT / 2);
      hit.eventMode = 'static';
      hit.cursor = 'pointer';
      // A world with its own map: the building opens the map, centred on it.
      hit.on('pointertap', () =>
        mapped ? this.enterWorld(world, node.id, x, y) : this.tapSlot(node),
      );
      this.landmarkLayer.addChild(hit);
      // Its slots' plates give way to the world's own plate.
      if (mapped) continue;
      const plaque = new RealmPlaque(
        l.name,
        open,
        open ? null : slotGemPrice(node.id),
        node.id === this.opts.currentNodeId,
        { lock, gem, floor: this.floors.loaded(world) },
      );
      plaque.label = `plaque:${l.name}`;
      plaque.position.set(x, y + PLAQUE_DROP);
      plaque.scale.set(1 / this.cam.zoom);
      plaque.eventMode = 'static';
      plaque.cursor = 'pointer';
      plaque.on('pointertap', () => this.tapSlot(node));
      plaques.push(plaque);
      this.plates.push({
        item: plaque,
        mx: x,
        my: y,
        drop: PLAQUE_DROP,
        box: boxOf(plaque),
        maxShift: PLATE_SHIFT,
        tappable: true,
        shown: false,
        fresh: true,
        world,
      });
    }
    if (this.overview) plaques.push(...this.worldPlates(lock, gem));
    // Plates over every tap area, so a neighbour's never hides one.
    for (const p of plaques) this.landmarkLayer.addChild(p);
  }

  /** Overview mode: each world's name as a plate with its open count. A
   *  world with a map opens it; any other, its board. */
  private worldPlates(lock: Texture | null, gem: Texture | null): RealmPlaque[] {
    const here = worldOf(this.opts.currentNodeId);
    return BIOMES.map((b, w) => {
      const slots = worldSlots(w);
      const open = slots.filter((n) => isSlotUnlocked(n.id, this.unlocked)).length;
      const mapped = this.mapped(w);
      const plaque = new RealmPlaque(
        b.name,
        true,
        null,
        mapped && w === here,
        { lock, gem, floor: this.floors.loaded(w) },
        { line: `${open} of ${slots.length} open` },
      );
      const at = WORLD_LABELS[b.id];
      const x = at.x * this.map.w;
      const y = at.y * this.map.h;
      plaque.label = `world:${b.name}`;
      plaque.scale.set(1 / this.cam.zoom);
      plaque.eventMode = 'static';
      plaque.cursor = 'pointer';
      plaque.on('pointertap', () => {
        if (this.dragged) return;
        if (mapped) this.enterWorld(w, null, x, y);
        else this.openBoard(w);
      });
      this.plates.push({
        item: plaque,
        mx: x,
        my: y,
        drop: 0,
        box: boxOf(plaque),
        maxShift: Math.max(PLATE_SHIFT, plaque.width / 2),
        tappable: true,
        shown: false,
        fresh: true,
        world: w,
      });
      return plaque;
    });
  }

  // ---- the side places, the secrets and the title ----

  private buildPlaces(opts: RealmPlacesOptions): void {
    const places = new RealmPlaces(
      {
        stage: this.app.stage,
        ticker: this.app.ticker,
        floor: (world) => this.floorFor(world),
        bezel: () => this.texture('bezel'),
        lock: () => this.texture('lock'),
        dragged: () => this.dragged,
        world: () => this.currentWorld,
        coverChanged: () => this.pauseAmbient(),
        relayout: () => this.measurePins(),
      },
      opts,
      this.map,
    );
    this.places = places;
    places.setCovered(this.hostPaused);
    // The floors already in (the opening world's).
    BIOMES.forEach((_, w) => {
      const t = this.floors.loaded(w);
      if (t) places.floorArrived(w, t);
    });
    // Glints sit on the painting, under the names; pins over the plates.
    this.world.addChildAt(places.glintLayer, this.world.getChildIndex(this.labelLayer));
    this.world.addChild(this.spotLayer);
    this.spotPlates = places.pins.map((pin) => {
      this.spotLayer.addChild(pin);
      return {
        item: pin,
        mx: pin.x,
        my: pin.y,
        drop: 0,
        box: boxOf(pin),
        // A pin may go a little further than a plate before it hides: it
        // floats over its spot, so it reads as that spot's from further.
        maxShift: PLATE_SHIFT * 1.4,
        tappable: true,
        shown: false,
        fresh: true,
        world: spotWorld(pin.spot),
      };
    });
    this.hud.addChild(places.crown, places.gift);
    this.app.stage.addChild(places.overlay);
    void places.init();
  }

  /** The Wheel and the gift down the right, under the wallet. */
  protected override layoutMore(w: number, _h: number, inset: MapInsets): Container[][] {
    if (!this.places) return [];
    const x = w - inset.right - 12 - 30;
    this.places.crown.position.set(x, inset.top + TOP_BAR + 34);
    this.places.gift.position.set(x, inset.top + TOP_BAR + 34 + 84);
    return [[this.places.crown, this.places.gift].filter((c) => c.visible)];
  }

  protected override moreControls(out: Map<string, Container>): void {
    if (this.places) {
      out.set('wheel', this.places.crown);
      out.set('gift', this.places.gift);
    }
  }

  /** A pin's look changed (its plate's words): measure it again. */
  private measurePins(): void {
    for (const p of this.spotPlates) p.box = boxOf(p.item);
    if (this.state === 'initialized') {
      this.layoutHud();
      this.placeNames();
    }
  }

  /** Fetch the player's realm again (the player has just loaded). */
  reloadPlaces(): void {
    void this.places?.reload();
  }

  /** The daily gift is there to claim, or isn't. */
  setGiftReady(ready: boolean): void {
    this.places?.setGiftReady(ready);
    if (this.state === 'initialized') this.layoutHud();
  }

  /** Centre the map on a side place's spot or a secret, at least at the
   *  opening zoom (tests and dev). */
  focusOn(id: string): void {
    const at = REALM_SPOTS.find((s) => s.id === id) ?? SECRET_SPOTS.find((s) => s.id === id);
    if (!at) return;
    const zoom = Math.max(this.cam.zoom, openingZoom(this.map, this.view));
    this.focus(at.x * this.map.w, at.y * this.map.h, zoom, false);
  }

  /** Where a side place's pin is on screen, or null while it's hidden
   *  (off screen, or crowded out by the controls) (tests). */
  spotPoint(id: string): { x: number; y: number } | null {
    const placed = this.spotPlates.find((p) => p.item.label === `spot:${id}`);
    if (!placed?.shown) return null;
    return this.places?.spotPoint(id) ?? null;
  }

  /** Where a secret's glint is on screen, or null once found (tests). */
  glintPoint(id: string): { x: number; y: number } | null {
    return this.places?.glintPoint(id) ?? null;
  }

  /** The open place card as plain data, or null (tests). */
  placeCard(): PlaceCardUi | null {
    return this.places?.cardUi ?? null;
  }

  /** Where a place card's button is on screen (tests). */
  placeCardButton(label: string): { x: number; y: number } | null {
    return this.places?.cardButtonPoint(label) ?? null;
  }

  /** Where a world's plate is on screen (overview mode), or null while
   *  it's hidden (tests). */
  worldPlatePoint(name: string): { x: number; y: number } | null {
    const p = this.plates.find((q) => q.item.label === `world:${name}`);
    if (!p?.shown) return null;
    const b = p.item.getBounds();
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }

  /** A point on screen on a world's own land: nothing else there takes the
   *  tap (no building, plate, pin or glint), or null when none is in view
   *  (tests). */
  landPoint(name: string): { x: number; y: number } | null {
    const world = BIOMES.findIndex((b) => b.name === name);
    if (world < 0) return null;
    const c = worldCentre(world);
    const boundary = new EventBoundary(this.app.stage);
    for (let ring = 0; ring < 12; ring++) {
      for (let k = 0; k < Math.max(1, ring * 6); k++) {
        const a = (k / Math.max(1, ring * 6)) * Math.PI * 2;
        const mx = c.x * this.map.w + Math.cos(a) * ring * 60;
        const my = c.y * this.map.h + Math.sin(a) * ring * 60;
        if (nearestWorld(mx / this.map.w, my / this.map.h) !== world) continue;
        const x = this.cam.x + mx * this.cam.zoom;
        const y = this.cam.y + my * this.cam.zoom;
        if (x < 40 || y < 120 || x > this.view.w - 40 || y > this.view.h - 140) continue;
        if (boundary.hitTest(x, y) === this.world) return { x, y };
      }
    }
    return null;
  }

  /** The world being glided into, before its map opens, or null (tests). */
  enteringWorld(): string | null {
    return this.entering === null ? null : (BIOMES[this.entering]?.name ?? null);
  }

  /** The player's realm as loaded (tests). */
  realmView(): unknown {
    return this.places?.realm ?? null;
  }

  /** The title plaque is showing (tests). */
  titleShown(): boolean {
    return this.places?.titleShown ?? false;
  }

  /** The mini-game on screen as plain data, or null (tests). */
  get miniGameUi(): MiniGameSnapshot | null {
    return this.places?.miniGameUi ?? null;
  }

  pressMiniGame(label: string): boolean {
    return this.places?.pressMiniGame(label) ?? false;
  }

  tapMiniGame(index: number): boolean {
    return this.places?.tapMiniGame(index) ?? false;
  }
}
